import { useEffect, useRef, useState } from "react";
import { useRouter } from "next/compat/router";
import { type z } from "zod";
import { SubmitHandler, useForm, useWatch } from "react-hook-form";
import { standardSchemaResolver } from "@hookform/resolvers/standard-schema";
import {
  Heading,
  Text,
  Box,
  Modal,
  Button,
  TextField,
  ErrorText,
} from "@cruk/cruk-react-components";

import { useModalContext } from "@fwa/src/contexts/ModalContext";
import { useFundraiserContext } from "@fwa/src/contexts/FundraiserContext";
import { useOptimizelyContext } from "@fwa/src/contexts/OptimizelyContext";
import { useTracking } from "@fwa/src/hooks/useTracking";
import { logOut } from "@fwa/src/services/auth";
import { FWS_BASE_URL, FWA_BASE_URL } from "@fwa/src/services/apiClient";
import {
  redirectStateFromUrl,
  urlParamsObjectAddEmParams,
  urlParamsObjectClearEmParams,
} from "@fwa/src/utils/urlUtils";
import { isBrowser } from "@fwa/src/utils/browserUtils";
import { RegexNoEmoji } from "@fwa/src/utils/formUtils";
import { emJourneyValidation } from "@fwa/src/validation/emJourney";
import { OAUTH_CLIENT_ID } from "@fwa/src/services/auth";

import { ModalNotLoggedIn } from "@fwa/src/components/ModalNotLoggedIn";
import { ModalWrongLogIn } from "@fwa/src/components/ModalWrongLogIn";

import sharedStyles from "@fwa/src/components/styles.module.css";

import type {
  FinaliseRegistrationType,
  FundraisingPageType,
} from "@fwa/src/types";

const modalPartyPopper = "/assets/images/img/party-popper.svg";

export const EventManagementJourneyModal = ({
  page,
  closeModalClearEmValueFromQueryString,
  closeModal,
  numberOfPages,
  reservationId,
}: {
  page: FundraisingPageType;
  closeModalClearEmValueFromQueryString: () => void;
  closeModal: () => void;
  numberOfPages: number;
  reservationId: string;
}) => {
  const [showTeamCreate, setShowTeamCreate] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { trackEventGtm, trackError } = useTracking();
  const { trackEventOptimizely } = useOptimizelyContext();
  const router = useRouter();

  const hasMultiplePages = numberOfPages > 1;

  type EventManagementJourneyValues = z.infer<typeof emJourneyValidation>;

  const initialValues: EventManagementJourneyValues = {
    team: undefined,
    teamTitle: undefined,
  };

  const formProps = useForm<EventManagementJourneyValues>({
    mode: "onChange",
    reValidateMode: "onBlur",
    defaultValues: initialValues,
    criteriaMode: "firstError",
    shouldFocusError: true,
    resolver: standardSchemaResolver(emJourneyValidation),
    resetOptions: {
      keepDirtyValues: true, // user-interacted input will be retained
      keepErrors: false, // input errors will be retained with value update
    },
  });
  const {
    getValues,
    handleSubmit,
    formState: { errors },
    register,
    control,
  } = formProps;

  const watchedTeamTitle = useWatch({
    control,
    name: "teamTitle",
  });
  const hasNoWeirdChars = RegexNoEmoji.pattern.test(watchedTeamTitle || "");
  const teamTitleHasWeirdChars = !hasNoWeirdChars;

  // Keeps react hook form happy
  const onSubmit = handleSubmit((data) => {
    submit(data);
    trackEventGtm({ event: "create_team_submit" });
    trackEventOptimizely({
      eventKey: "of_em_journey_create_team_submit",
    });
    return;
  });

  // Keeps react hook form happy
  const submit: SubmitHandler<EventManagementJourneyValues> = async (
    values,
    e,
  ): Promise<void> => {
    if (e) e.preventDefault();
    await createTeam();
  };

  const createTeam = async () => {
    setIsSubmitting(true);
    const values = getValues();

    const teamCreationForRegistrationGroup = await fetch(
      `${FWS_BASE_URL}/fundraisingpages/${page.uniqueId}/eventreservations/${reservationId}/finaliseeventregistration`,
      {
        method: "POST",
        body: JSON.stringify({
          //  codeChallenge: challenge(),
          clientId: OAUTH_CLIENT_ID, // can't be blank
          //  ...(values?.team?.uniqueId.length && {
          //    teamUniqueId: values.team.uniqueId,
          teamTitle: values.teamTitle,
        }),
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      },
    )
      .then((res) => {
        if (!res.ok) {
          throw new Error(res.status.toString());
        }
        return res;
      })
      .then((res) => res.json())
      .then((data) => data as FinaliseRegistrationType)
      .catch((err) => {
        const error = err as Error;
        trackError(error, { component: "EventManagementJourney" });
        // redirect to the primary page specified in the url as a hail mary
        // so they can hopefully start fundraising despite this finalise reg error
        router?.push({
          pathname: page.url.replace(FWA_BASE_URL, ""),
          query: urlParamsObjectAddEmParams(router.query, {
            numberOfPages,
            eventName: page?.event?.eventName || undefined,
            reservationId: reservationId || undefined,
            waveCode: page?.waveCode || undefined,
          }),
        });
        return;
      });

    if (teamCreationForRegistrationGroup && router) {
      // Redirect to the page or team
      const relativeTeamUrl = teamCreationForRegistrationGroup.page.url.replace(
        FWA_BASE_URL,
        "",
      );
      // wipe all query params not just EM ones because 'slug' of a fundraising page would cause routing problems on team path
      const query = {};
      router
        ?.push(
          {
            pathname: relativeTeamUrl,
            query,
          },
          undefined,
          {},
        )
        .then(() => {
          // now clear
          closeModal();
          return;
        })
        .catch((err) => {
          trackError(err as Error, {
            component: "EventManagementJourneyModal",
            function: "createTeam redirect",
          });
        });
    }
  };

  const joinTeam = async () => {
    trackEventGtm({ event: "join_team_cta" });
    router
      ?.push({
        pathname: `/team-search`,
        query: {
          // this changes standard join to team to group join team API
          ...(hasMultiplePages && { rid: reservationId }),
          fp: page.uniqueId,
        },
      })
      .then(() => {
        // now close modal
        // we don't need to clear EM fields from query string
        // because we are setting new params above
        closeModal();
        return;
      })
      .catch((err) => {
        trackError(err as Error, {
          component: "EventManagementJourneyModal",
          function: "join team redirect",
        });
      });
  };

  return (
    <Modal
      closeFunction={closeModalClearEmValueFromQueryString}
      modalName="Event Giving Page signup modal"
      startOpen
    >
      <Box data-component="em-modal-page" margin="s">
        <Heading textAlign="center">
          {hasMultiplePages
            ? `Your group's Giving Pages are ready!`
            : `Your Giving Page is ready!`}
        </Heading>
        <Box paddingBottom="xxs">
          <div
            style={{
              display: "grid",
              placeItems: "center",
              justifyContent: "center",
              gridTemplateColumns: "1fr",
              paddingTop: "2em",
            }}
          >
            <img
              className="spacing-props"
              data-margin-bottom="s"
              data-margin-top="none"
              data-margin-horizontal="auto"
              alt=""
              width="90px"
              height="90px"
              style={{
                display: "block",
                width: "90px",
              }}
              src={modalPartyPopper}
            />
            <Box paddingVertical="s">
              <Button
                data-cta-type="view-giving-page"
                appearance="secondary"
                onClick={() => {
                  trackEventGtm({ event: "fundraising_page_cta" });

                  closeModalClearEmValueFromQueryString();
                }}
              >
                View my Giving Page
              </Button>
            </Box>
          </div>
          <div className={sharedStyles["divider"]}>OR</div>

          <Heading h3 textAlign="center">{`Raise more with a Team`}</Heading>

          {showTeamCreate ? (
            <>
              <Text>
                {`Give your Team a name - you can change this anytime.`}
              </Text>
              <form
                noValidate
                onSubmit={(e) => {
                  onSubmit(e).catch((err) => {
                    trackError(err as Error, {
                      component: "EventManagementJourneyModal",
                      function: "onSubmit team name form",
                    });
                  });
                }}
              >
                <Box>
                  <div className={sharedStyles["field-wrapper"]}>
                    <TextField
                      {...register("teamTitle")}
                      errorMessage={errors.teamTitle?.message}
                      label="Team name"
                      required
                    />
                    {/* Technically, we do allow emojis just not only emojis, so lets show an additional warning */}
                    {teamTitleHasWeirdChars ? (
                      <ErrorText marginTop="xxs">
                        Emojis in your Team name may disappear on printed
                        materials but will show on your page.
                      </ErrorText>
                    ) : null}
                  </div>
                </Box>
                <Box>
                  <Text>
                    {`After creating your Team, you'll see your team page. Let your team know they can find it on their giving page.`}
                  </Text>
                </Box>
                <div
                  style={{
                    display: "grid",
                    placeItems: "center",
                    justifyContent: "center",
                    gridTemplateColumns: "1fr",
                    paddingBottom: "2em",
                  }}
                >
                  <Button
                    appearance="primary"
                    type="submit"
                    disabled={isSubmitting}
                    data-cta-type="create-team-submit"
                  >
                    Create my Team
                  </Button>
                </div>
              </form>
            </>
          ) : (
            <>
              <Text marginBottom="m">
                {`Team fundraisers raise 50% more than solo fundraisers. Create or join a Team to see shared totals and compete on a leaderboard.`}
              </Text>
              <div
                style={{
                  display: "grid",
                  placeItems: "center",
                  justifyContent: "center",
                  gridTemplateColumns: "1fr",
                  gap: "2em",
                }}
              >
                <Button
                  data-cta-type="create-team"
                  appearance="primary"
                  onClick={() => {
                    setShowTeamCreate(true);
                  }}
                >
                  Create a Team
                </Button>
                <Button
                  data-cta-type="join-team"
                  appearance="secondary"
                  onClick={() => {
                    joinTeam().catch((err) => {
                      trackError(err as Error, {
                        component: "EventManagementJourneyModal",
                        function: "joinTeam redirect",
                      });
                    });
                  }}
                >
                  Join a Team
                </Button>
              </div>
            </>
          )}
        </Box>
      </Box>
    </Modal>
  );
};

export const EventManagementJourneyModalManager = ({
  page,
}: {
  page: FundraisingPageType;
}) => {
  const [modalState, setModalState] = useModalContext();
  const [fundraiserState, setFundraiserState] = useFundraiserContext();
  const { loggedInStatus, fundraiser } = fundraiserState;
  const { trackError } = useTracking();
  const router = useRouter();
  const isDismissingRef = useRef(false);
  const { trackEventOptimizely } = useOptimizelyContext();

  useEffect(() => {
    if (
      !router ||
      !isBrowser ||
      !page ||
      loggedInStatus === "unknown" ||
      modalState ||
      isDismissingRef.current
    ) {
      return;
    }

    const { numberOfPages, isEmJourney, reservationId } = redirectStateFromUrl(
      window.location.href,
    );

    if (!isEmJourney || !reservationId) {
      return;
    }

    const isLoggedOut = loggedInStatus === "loggedOut";
    const isNewUser = !page.fundraiser.isActivated || false; // if the fundraiser isn't activated they must be a new user
    const loggedInWithIncorrectAccountAndFundraiser =
      loggedInStatus === "loggedIn" &&
      page?.fundraiser &&
      fundraiser &&
      fundraiser?.uniqueId !== page.fundraiser.uniqueId;
    const loggedInWithIncorrectAccountNoFundraiserFound =
      loggedInStatus === "loggedIn" && !fundraiser;
    const isCorrectLogInForRegistration =
      !loggedInWithIncorrectAccountNoFundraiserFound &&
      !loggedInWithIncorrectAccountAndFundraiser;

    const dismissModal = () => {
      // flag to stop infinite loop when dismiss triggers infinite loop when we update the query string
      isDismissingRef.current = true;
      trackEventOptimizely({
        eventKey: "of_em_journey_fundraising_page_submit",
      });
      setModalState(null);
      return;
    };

    const dismissModalClearEmValueFromQueryString = () => {
      // flag to stop infinite loop when dismiss triggers infinite loop when we update the query string
      isDismissingRef.current = true;

      trackEventOptimizely({
        eventKey: "of_em_journey_fundraising_page_submit",
      });
      router
        ?.push(
          {
            // clear the EM params from the query string when we dismiss the modal
            query: urlParamsObjectClearEmParams(router.query),
          },
          undefined,
          {
            shallow: true,
          },
        )
        .then(async () => {
          if (loggedInWithIncorrectAccountNoFundraiserFound) {
            await logOut();
            setFundraiserState({
              oauthUser: null,
              fundraiser: null,
              loggedInStatus: "loggedOut",
            });
            router.reload();
          }
          setModalState(null);
          return;
        })
        .catch((error) => {
          trackError(error, {
            component: "EventManagementJourneyModalManager",
            function: "dismissModal",
          });
        });
    };

    const modalType: "loggedOut" | "wrongLogIn" | "success" = isLoggedOut
      ? "loggedOut"
      : !isCorrectLogInForRegistration
        ? "wrongLogIn"
        : "success";

    const emModalContent = (
      <EventManagementJourneyModal
        page={page}
        closeModalClearEmValueFromQueryString={
          dismissModalClearEmValueFromQueryString
        }
        closeModal={dismissModal}
        numberOfPages={numberOfPages}
        reservationId={reservationId}
      />
    );

    switch (modalType) {
      case "loggedOut":
        setModalState(
          <ModalNotLoggedIn
            closeFunction={dismissModalClearEmValueFromQueryString}
            isNewUser={isNewUser}
            modalName="Event Giving Page signup modal not logged in"
            componentName="em-page-modal-not-logged-in"
          />,
        );
        break;
      case "wrongLogIn":
        setModalState(
          <ModalWrongLogIn
            closeFunction={dismissModalClearEmValueFromQueryString}
            modalName="Event Giving Page signup modal wrong log in"
            componentName="em-page-modal-wrong-log-in"
          />,
        );
        break;
      default:
        setModalState(emModalContent);
    }
  }, [
    router,
    loggedInStatus,
    fundraiser,
    setFundraiserState,
    setModalState,
    page,
    modalState,
    trackError,
    trackEventOptimizely,
  ]);

  return null;
};

export default EventManagementJourneyModalManager;
